import { toDayNum, fromDayNum } from './fsConstraints';
import { getNumericRef } from '../pages/Client/components/gantt/GanttUtils';

/**
 * FS clamp for TYPED dates saved through the task modal and the Gantt's inline
 * date cell (Bill, 2026-07-29, reworked 2026-08-03).
 *
 * A typed start stops at the day after its latest source's end; a typed due date
 * stops at the day before its earliest target's start. Nothing else ever moves -
 * the modal clamps, it never pushes a dependency chain the way a bar resize does.
 *
 * Reworked 2026-08-03 for three reasons:
 *   1. The modal sends `due_date`, this read `dueDate`, so the end clamp was dead.
 *      Read whichever key arrived and write the clamped value back to that SAME
 *      key - admin-tasks maps `due_date` only and silently drops `dueDate`.
 *   2. Start and end now save as one payload, so the pair must be clamped against
 *      the PROPOSED pair. Clamping each against the stored record (what
 *      clampStart/clampEnd do) turns 10th-14th -> 3rd-7th into 3rd-10th.
 *   3. Nothing is corrected silently. Every adjustment is reported back so the
 *      caller can confirm it, and an end before a start is refused outright.
 *
 * tasks: the project task list (DynamoDB field names or gantt names)
 * links: canonical link objects from buildLinksFromSubtasks
 * updates: the modal's update payload - only `start` and the due-date key are read
 *
 * Returns { updates, adjustments, impossible, endBeforeStart }:
 *   adjustments    [{ field, from, to, boundBy }] - dates the clamp moved
 *   impossible     { typedDays, availableDays, boundBy } when the typed range
 *                  cannot fit between its neighbours
 *   endBeforeStart { start, end, typed, clampedBy } when the result would end
 *                  before it starts (D9). Mutually exclusive with `impossible`.
 * When either is set, `adjustments` is empty and `updates` comes back unchanged -
 * the caller must write nothing.
 */
export function clampTaskDateUpdates(tasks, links, taskId, updates) {
    const untouched = { updates, adjustments: [], impossible: null, endBeforeStart: null };
    if (!updates) return untouched;

    // The modal sends `due_date`; other callers may use `dueDate`
    const dueKey = 'due_date' in updates ? 'due_date' : 'dueDate';
    const hasStart = 'start' in updates && updates.start;
    const hasDue = !!updates[dueKey];
    if (!hasStart && !hasDue) return untouched;

    const task = (tasks || []).find(t => t.taskId === taskId);
    if (!task || task.type !== 'subtask') return untouched;

    const tasksByKey = {};
    const nameByKey = {};
    for (const t of tasks) {
        if (t.type !== 'subtask') continue;
        const s = t.startDate || t.start;
        const e = t.dueDate || t.end;
        if (!s && !e) continue;
        const k = String(getNumericRef(t.taskId));
        tasksByKey[k] = {
            start: toDayNum(s || e),
            end: toDayNum(e || s)
        };
        nameByKey[k] = t.subject || t.text || t.taskId;
    }
    const edges = [];
    for (const l of links || []) {
        const sKey = String(getNumericRef(l.source));
        const tKey = String(getNumericRef(l.target));
        if (tasksByKey[sKey] && tasksByKey[tKey]) edges.push({ source: sKey, target: tKey });
    }

    const key = String(getNumericRef(taskId));
    const stored = tasksByKey[key];
    if (!stored) return untouched;

    // The legal window from the dependency graph ALONE. Deliberately not
    // clampStart/clampEnd: those also bound by the task's own stored dates,
    // which is what breaks a pair edit (see note 2 above).
    let lo = -Infinity;
    let loBy = null;
    let hi = Infinity;
    let hiBy = null;
    for (const e of edges) {
        if (e.target === key) {
            const src = tasksByKey[e.source];
            if (src && src.end != null && src.end + 1 > lo) {
                lo = src.end + 1;
                loBy = nameByKey[e.source];
            }
        }
        if (e.source === key) {
            const tgt = tasksByKey[e.target];
            if (tgt && tgt.start != null && tgt.start - 1 < hi) {
                hi = tgt.start - 1;
                hiBy = nameByKey[e.target];
            }
        }
    }

    const typedStart = hasStart ? toDayNum(updates.start) : null;
    const typedEnd = hasDue ? toDayNum(updates[dueKey]) : null;

    // The pair as the user has asked for it: typed where typed, stored otherwise
    const proposedStart = typedStart != null ? typedStart : stored.start;
    const proposedEnd = typedEnd != null ? typedEnd : stored.end;

    // D9, checked BEFORE the window fit: an inverted pair must never be reported
    // as a gap problem - with no dependencies lo/hi are infinite and the
    // available-days figure would be meaningless.
    if (proposedStart > proposedEnd) {
        return {
            updates,
            adjustments: [],
            impossible: null,
            endBeforeStart: {
                start: fromDayNum(proposedStart),
                end: fromDayNum(proposedEnd),
                typed: null,
                clampedBy: null
            }
        };
    }

    // Clamp only the sides the user actually typed
    const finalStart = typedStart != null ? Math.max(typedStart, lo) : stored.start;
    const finalEnd = typedEnd != null ? Math.min(typedEnd, hi) : stored.end;

    if (finalStart > finalEnd) {
        // Both typed: the range itself will not fit between the neighbours
        if (typedStart != null && typedEnd != null) {
            const available = Number.isFinite(lo) && Number.isFinite(hi) ? (hi - lo) + 1 : null;
            return {
                updates,
                adjustments: [],
                impossible: {
                    typedDays: (typedEnd - typedStart) + 1,
                    availableDays: available,
                    boundBy: loBy || hiBy
                },
                endBeforeStart: null
            };
        }
        // One side typed: the clamp pushed it past the task's other date. Report
        // what was typed as well as where it landed, or the message names a date
        // the user never entered.
        const movedStart = typedStart != null;
        return {
            updates,
            adjustments: [],
            impossible: null,
            endBeforeStart: {
                start: fromDayNum(finalStart),
                end: fromDayNum(finalEnd),
                typed: fromDayNum(movedStart ? typedStart : typedEnd),
                clampedBy: movedStart ? loBy : hiBy
            }
        };
    }

    const adjustments = [];
    const out = { ...updates };
    if (typedStart != null) {
        if (finalStart !== typedStart) {
            adjustments.push({
                field: 'start',
                from: fromDayNum(typedStart),
                to: fromDayNum(finalStart),
                boundBy: loBy
            });
        }
        out.start = fromDayNum(finalStart);
    }
    if (typedEnd != null) {
        if (finalEnd !== typedEnd) {
            adjustments.push({
                field: 'end',
                from: fromDayNum(typedEnd),
                to: fromDayNum(finalEnd),
                boundBy: hiBy
            });
        }
        out[dueKey] = fromDayNum(finalEnd);
    }

    return { updates: out, adjustments, impossible: null, endBeforeStart: null };
}
